import Phaser from 'phaser';
import type { BossCandy } from '@/net/api';

/** One short-lived, clearly marked obstacle. All effects belong to the arena. */
export class HalloweenRottenCandyFx {
  private retired = false;
  private pending = false;
  private readonly baseScaleX: number;
  private readonly baseScaleY: number;
  private readonly fumes: Phaser.GameObjects.Arc[];
  private readonly badge: Phaser.GameObjects.Text;

  constructor(private readonly scene: Phaser.Scene, private readonly candy: BossCandy,
    private readonly sprite: Phaser.GameObjects.Image, private readonly glow: Phaser.GameObjects.Arc) {
    this.baseScaleX = sprite.scaleX; this.baseScaleY = sprite.scaleY;
    this.fumes = [0, 1, 2].map(() => scene.add.circle(candy.x, candy.y, 5, 0xa7ce6a, 0.5).setDepth(6));
    this.badge = scene.add.text(candy.x + 27, candy.y - 28, '−10', {
      fontFamily: 'Nunito, sans-serif', fontSize: '20px', fontStyle: 'bold',
      color: '#36572a', backgroundColor: '#ecf8bf', padding: { x: 4, y: 1 },
      stroke: '#ecf8bf', strokeThickness: 2,
    }).setOrigin(0.5).setDepth(7);
  }

  update(now: number): void {
    if (this.retired) return;
    if (now >= (this.candy.expiresAt ?? 0)) { this.retire(); return; }
    if (this.pending) return;
    const age = Math.max(0, now - this.candy.availableAt);
    const wave = Math.sin(age / 125);
    this.glow.setScale(1 + wave * 0.12).setAlpha(0.24 + wave * 0.08);
    this.badge.setScale(1 + wave * 0.05);
    this.fumes.forEach((fume, i) => {
      const progress = ((age + i * 300) % 900) / 900;
      fume.setPosition(this.candy.x - 22 + i * 22 + Math.sin(progress * Math.PI * 2) * 7,
        this.candy.y - 12 - progress * 55).setAlpha((1 - progress) * 0.55).setScale(0.6 + progress * 0.7);
    });
    if (now >= this.candy.availableAt) {
      this.sprite.setPosition(this.candy.x, this.candy.y + wave * 4)
        .setAngle(wave * 12).setScale(this.baseScaleX * (1 + wave * 0.07), this.baseScaleY * (1 - wave * 0.07));
    }
  }

  /** Hide ground effects while the immediate pickup awaits server confirmation. */
  setPending(pending: boolean): void {
    if (this.retired) return;
    this.pending = pending;
    [this.sprite, this.glow, this.badge, ...this.fumes].forEach(object => object.setVisible(!pending));
  }

  retire(): void {
    if (this.retired) return;
    if (this.pending) { this.destroy(); return; }
    this.retired = true;
    this.fumes.forEach(fume => fume.destroy()); this.badge.destroy(); this.glow.destroy();
    this.scene.tweens.killTweensOf(this.sprite);
    for (let i = 0; i < 8; i++) {
      const angle = i * Math.PI / 4;
      const crumb = this.scene.add.circle(this.sprite.x, this.sprite.y, 3 + i % 3, i % 2 ? 0x9cb85d : 0x628b3b, 0.8).setDepth(6);
      this.scene.tweens.add({ targets: crumb, x: crumb.x + Math.cos(angle) * 35,
        y: crumb.y + Math.sin(angle) * 20 + 20, scale: 0, alpha: 0, duration: 550,
        onComplete: () => crumb.destroy() });
    }
    this.scene.tweens.add({ targets: this.sprite, scaleX: this.baseScaleX * 1.3,
      scaleY: 0, alpha: 0, angle: 45, duration: 420, ease: 'Quad.easeIn',
      onComplete: () => this.sprite.destroy() });
  }

  destroy(): void {
    this.retired = true;
    this.fumes.forEach(fume => fume.destroy()); this.badge.destroy(); this.glow.destroy();
    this.scene.tweens.killTweensOf(this.sprite); this.sprite.destroy();
  }
}
